import formatter from '@/lib/formatter';
import React from 'react';
import {motion} from 'framer-motion';
import {Doughnut} from 'react-chartjs-2';
import {Chart as ChartJS, type ChartData} from 'chart.js';
import {
  Book,
  Calendar1Icon,
  CalendarClock,
  CalendarCogIcon,
  CircleHelpIcon,
  HandshakeIcon,
  LucideIcon,
  MapPin,
  Receipt,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  XCircle,
  PauseCircle,
  EyeIcon,
  HelpCircle,
} from 'lucide-react';
import {StatusSelectForm} from '../../tasks/components/status-select-form';
import {apiService} from '@/lib/ApiService';
import {useQuery} from '@tanstack/react-query';
import {TaskStatuses, TaskStatusColors} from '../../tasks/types';
import {ContractStatuses, ContractStatusColors} from '../../project_contracts/types';

interface InfoViewProps {
  icon?: LucideIcon;
  label: string;
  labelColor?: string;
  value: React.ReactNode;
  valueColor?: string;
}

interface ProjectSummaryType {
  tasks: {
    by_status: {
      completed: number;
      in_progress: number;
      pending: number;
      [key: string]: number;
    };
    total: number;
  };
  contracts: {
    by_status: {
      [key: string]: number;
    };
    total: number;
    total_value: number;
    latest?: {
      id: number;
      name: string;
      contract_number: string;
      value: string;
      status: string;
      start_date: string;
      end_date: string;
    };
  };
}

const InfoView: React.FC<InfoViewProps> = ({icon: Icon, label, labelColor, value, valueColor}) => {
  return (
    <div className="flex flex-row items-center space-x-1">
      {Icon && <Icon className="h-5 w-5 shrink-0 text-primary self-center" />}
      <div
        className="text-lg font-medium whitespace-nowrap text-gray-600"
        style={{color: labelColor}}>
        {label}:
      </div>
      <div className="text-lg" style={{color: valueColor}}>
        {value}
      </div>
    </div>
  );
};

function Progress({completed, total, label}) {
  const progressPercentage = Math.round((completed / total) * 100) || 0;
  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="text-lg font-semibold mb-2">
        {completed} / {total} {label}
      </div>
      <div className="text-sm text-gray-600 mb-2">{progressPercentage}%</div>
      <div className="relative bg-gray-200 rounded-full h-4">
        <div
          className="bg-primary rounded-full h-4"
          style={{width: `${progressPercentage}%`}}></div>
      </div>
    </div>
  );
}

function ProjectSummarySection({projectId}) {
  // Use react-query hook to fetch project summary data
  const {
    data: summary,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['projectSummary', projectId],
    queryFn: async () => {
      const response = await apiService.projects.projectSummary(projectId);
      return response as ProjectSummaryType;
    },
    staleTime: 5 * 60 * 1000, // Data considered fresh for 5 minutes
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  if (isLoading) {
    return (
      <div className="p-6 bg-white rounded-lg border w-full flex justify-center items-center min-h-[300px]">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mb-2"></div>
          <p>Đang tải dữ liệu...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-white rounded-lg border w-full text-red-500">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          <p>
            Đã xảy ra lỗi khi tải dữ liệu:{' '}
            {error instanceof Error ? error.message : 'Vui lòng thử lại sau.'}
          </p>
        </div>
      </div>
    );
  }

  if (!summary) {
    return <div className="p-6 bg-white rounded-lg border w-full">Không có dữ liệu</div>;
  }

  // Tasks section data preparation
  const taskCompleted = summary.tasks.by_status.completed || 0; // Keep for Progress component
  const taskTotal = summary.tasks.total;

  const taskChartData: ChartData<'doughnut'> = {
    labels: TaskStatuses.map(status => status.label),
    datasets: [
      {
        data: TaskStatuses.map(status => summary.tasks.by_status[status.id] || 0),
        backgroundColor: TaskStatuses.map(status => TaskStatusColors[status.id] || '#CCCCCC'),
        borderWidth: 2,
      },
    ],
  };

  // Contracts section data preparation
  const contractsTotal = summary.contracts.total || 0;

  // Prepare contract data using the defined ContractStatuses
  const contractChartData: ChartData<'doughnut'> = {
    labels: ContractStatuses.map(status => status.label),
    datasets: [
      {
        data: ContractStatuses.map(status => summary.contracts.by_status[status.id] || 0),
        backgroundColor: ContractStatuses.map(
          status => ContractStatusColors[status.id] || '#CCCCCC',
        ),
        borderWidth: 2,
      },
    ],
  };

  return (
    <div className="p-6 bg-white rounded-lg border w-full">
      <h2 className="text-2xl font-semibold mb-6">Tổng quan dự án</h2>

      {/* Tasks Section */}
      <div className="border-b pb-6 mb-6">
        <h3 className="text-xl font-semibold mb-4 flex items-center">
          <CheckCircle className="h-5 w-5 mr-2 text-primary" />
          Tình trạng công việc
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Tasks Chart */}
          <div className="max-h-[300px] flex flex-col items-center justify-center relative">
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
              <div className="text-3xl font-bold">{taskTotal}</div>
              <div className="text-sm text-gray-500">tổng số</div>
            </div>
            <Doughnut
              data={taskChartData}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                  legend: {
                    display: false, // Disable the default chart legend
                  },
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        const label = context.label || '';
                        const value = context.raw as number;
                        const percentage = Math.round((value / taskTotal) * 100);
                        return `${label}: ${value} (${percentage}%)`;
                      },
                    },
                  },
                },
                cutout: '70%',
              }}
            />
          </div>

          {/* Tasks Stats & Progress */}
          <div className="flex flex-col gap-4">
            <Progress completed={taskCompleted} total={taskTotal} label="Công việc hoàn thành" />

            {/* React-based legend for Tasks */}
            <div className="mt-4">
              <h4 className="font-semibold mb-2 text-sm text-gray-600">
                Chi tiết theo trạng thái (Tổng: {taskTotal})
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                {TaskStatuses.map(status => {
                  const count = summary.tasks.by_status[status.id] || 0;
                  const color = TaskStatusColors[status.id] || '#CCCCCC'; // Fallback color

                  return (
                    <div key={status.id} className="flex items-center text-sm py-1">
                      <span
                        className="inline-block w-3 h-3 mr-2 rounded-sm shrink-0"
                        style={{backgroundColor: color}}></span>
                      <span className="text-gray-700">
                        {status.label} ({count})
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contracts Section */}
      <div>
        <h3 className="text-xl font-semibold mb-4 flex items-center">
          <FileText className="h-5 w-5 mr-2 text-primary" />
          Tình trạng hợp đồng
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Contracts Chart */}
          {contractsTotal > 0 ? (
            <div className="max-h-[300px] flex flex-col items-center justify-center relative">
              <div className="flex items-center justify-between my-2">
                <span className="font-medium">Tổng giá trị hợp đồng: </span>
                <span className="font-bold text-green-700">
                  {formatter.formatNumber(Number(summary.contracts.total_value))}
                </span>
              </div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
                <div className="text-3xl font-bold">{contractsTotal}</div>
                <div className="text-sm text-gray-500">tổng số</div>
              </div>
              <Doughnut
                data={contractChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: true,
                  plugins: {
                    legend: {
                      display: false, // Disable the default chart legend
                    },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          const label = context.label || '';
                          const value = context.raw as number;
                          const percentage = Math.round((value / contractsTotal) * 100);
                          return `${label}: ${value} (${percentage}%)`;
                        },
                      },
                    },
                  },
                  cutout: '70%',
                }}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-[200px] bg-gray-50 rounded-lg">
              <p className="text-gray-500">Không có dữ liệu hợp đồng</p>
            </div>
          )}

          {/* Contract Details */}
          <div className='h-full'>
            {summary.contracts.latest ? (
              <div className="bg-white rounded-lg border p-4">
                <h4 className="text-lg font-semibold mb-3">Hợp đồng mới nhất</h4>
                <div className="text-sm space-y-2">
                  <p>
                    <span className="font-medium">Tên:</span> {summary.contracts.latest.name}
                  </p>
                  <p>
                    <span className="font-medium">Mã HĐ:</span>{' '}
                    {summary.contracts.latest.contract_number}
                  </p>
                  <p>
                    <span className="font-medium">Giá trị:</span>{' '}
                    {formatter.formatNumber(Number(summary.contracts.latest.value))}
                  </p>
                  <p>
                    <span className="font-medium">Trạng thái:</span>{' '}
                    {summary.contracts.latest.status}
                  </p>
                  <p>
                    <span className="font-medium">Thời gian:</span>{' '}
                    {formatter.formatDate(summary.contracts.latest.start_date)} -{' '}
                    {formatter.formatDate(summary.contracts.latest.end_date)}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px] bg-gray-50 rounded-lg">
                <p className="text-gray-500">Không có dữ liệu hợp đồng</p>
              </div>
            )}
            {/* React-based legend for Contracts */}
            {contractsTotal > 0 && (
              <div className="md:col-span-2 mt-4">
                <h4 className="font-semibold mb-2 text-sm text-gray-600">
                  Chi tiết theo trạng thái (Tổng: {contractsTotal})
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1">
                  {ContractStatuses.map(status => {
                    const count = summary.contracts.by_status[status.id] || 0;
                    if (count === 0) return null; // Don't show statuses with 0 count

                    const color = ContractStatusColors[status.id] || '#CCCCCC'; // Fallback color
                    return (
                      <div key={status.id} className="flex items-center text-sm py-1">
                        <span
                          className="inline-block w-3 h-3 mr-2 rounded-sm shrink-0"
                          style={{backgroundColor: color}}></span>
                        <span className="text-gray-700">
                          {status.label} ({count})
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectOverview({project}) {
  if (!project) return null;
  return (
    <motion.div
      className="flex flex-row flex-wrap gap-2"
      initial={{
        opacity: 0,
        width: '70%',
      }}
      animate={{
        opacity: 1,
        width: '100%',
        transition: {
          duration: 1,
          ease: 'easeOut',
        },
      }}>
      <div className="p-6 bg-white rounded-lg border w-full">
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="flex flex-row col-span-2 p-3 bg-primary rounded-lg gap-6">
            <div>
              <p className="text-white text-lg">Mã dự án</p>
              <p className="text-white text-lg font-medium">{project.id}</p>
            </div>
            <div className="w-[1px] bg-white" />
            <div>
              <p className="text-white text-lg">Tên dự án</p>
              <p className="text-white text-lg font-medium">{project.name}</p>
            </div>
          </div>
          <InfoView icon={MapPin} label="Địa điểm" value={project.location} />
          <InfoView
            icon={HandshakeIcon}
            label="Khách hàng"
            value={<span className="text-blue-600">{project.customer}</span>}
          />
          <InfoView
            icon={Receipt}
            label="Vốn đầu tư"
            value={formatter.formatNumber(project.investment)}
          />
          <InfoView
            icon={CircleHelpIcon}
            label="Trạng thái"
            value={
              <StatusSelectForm
                resource="projects"
                id={project.id}
                initialStatus={project.status}
                label={''}
              />
            }
          />
          <InfoView
            icon={CalendarCogIcon}
            label="Ngày tạo"
            value={formatter.formatDate(project.created_at)}
          />
          <InfoView
            icon={Calendar1Icon}
            label="Ngày bắt đầu"
            value={formatter.formatDate(project.start_date)}
          />
          <InfoView
            icon={CalendarClock}
            label="Hạn hoàn thành"
            value={formatter.formatDate(project.expected_end_date)}
          />
        </div>

        <InfoView
          icon={Book}
          label="Mô tả"
          value={<p className="whitespace-pre-line">{project.description || 'Không có mô tả'}</p>}
        />
      </div>

      <ProjectSummarySection projectId={project.id} />
    </motion.div>
  );
}

export default ProjectOverview;
